import React, { createContext, useContext, useState, useCallback } from 'react';
import { AlertDialog } from '../components/AlertDialog';
import { ConfirmDialog } from '../components/ConfirmDialog';

const DialogContext = createContext(null);

export function DialogProvider({ children }) {
  const [alert, setAlert] = useState({ open: false, title: '', message: '' });
  const [confirm, setConfirm] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: () => {},
    onCancel: () => {},
  });

  const showAlert = useCallback((title, message) => {
    setAlert({ open: true, title: title || '', message: message || '' });
  }, []);

  const hideAlert = useCallback(() => {
    setAlert((a) => ({ ...a, open: false }));
  }, []);

  const showConfirm = useCallback(({ title, message, onConfirm, onCancel }) => {
    setConfirm({
      open: true,
      title: title || '',
      message: message || '',
      onConfirm: onConfirm || (() => {}),
      onCancel: onCancel || (() => setConfirm((c) => ({ ...c, open: false }))),
    });
  }, []);

  const hideConfirm = useCallback(() => {
    setConfirm((c) => ({ ...c, open: false }));
  }, []);

  const handleConfirm = useCallback(() => {
    confirm.onConfirm();
    hideConfirm();
  }, [confirm.onConfirm, hideConfirm]);

  const handleConfirmCancel = useCallback(() => {
    confirm.onCancel();
    hideConfirm();
  }, [confirm.onCancel, hideConfirm]);

  const value = {
    showAlert,
    hideAlert,
    showConfirm,
    hideConfirm,
  };

  return (
    <DialogContext.Provider value={value}>
      {children}
      <AlertDialog open={alert.open} title={alert.title} message={alert.message} onClose={hideAlert} />
      <ConfirmDialog
        open={confirm.open}
        title={confirm.title}
        message={confirm.message}
        onConfirm={handleConfirm}
        onCancel={handleConfirmCancel}
      />
    </DialogContext.Provider>
  );
}

export function useDialog() {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error('useDialog must be used within DialogProvider');
  return ctx;
}

export default DialogContext;
